import {FC} from 'react'
import {UserExtended, UserPrivacyFormValidationSchema} from '@/features/users/types.ts'
import {StyledUserDetailsFieldsGrid} from '@/features/users/components/user-details-information-privacy/style.ts'
import {CheckIcon, RefreshCw05Icon} from '@components/ui/icon/Icon.tsx'
import {useTranslation} from '@/translations/i18n'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {UserPrivacyForm} from '@/features/users/components/user-privacy-form/UserPrivacyForm'
import {DetailsField, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {zodResolver} from '@hookform/resolvers/zod'
import {getUserDirtyFieldsFromFormValues, remapUserToDefaultValues, remapUserToPayload} from '@/features/users/utils'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {useUserFormSubmission} from '@/features/users/hooks/useUserFormSubmission'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTheme} from 'styled-components'

interface UserDetailsInformationPrivacy {
    user: UserExtended
}

export const UserDetailsInformationPrivacy: FC<UserDetailsInformationPrivacy> = ({user}) => {
    const form = useForm<UserPrivacyFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserPrivacyFormValidationSchema),
        defaultValues: UserPrivacyFormValidationSchema.parse(remapUserToDefaultValues(user))
    })

    return (
        <FormProvider {...form}>
            <UserDetailsInformationPrivacyContent user={user} />
        </FormProvider>
    )
}

const UserDetailsInformationPrivacyContent = ({user}: {user: UserExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('privacy')
    const form = useFormContext<UserPrivacyFormValidationSchema>()
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useUserFormSubmission({
        formSchema: UserPrivacyFormValidationSchema
    })

    // mutation
    const updateUser = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getUserDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapUserToPayload(dirtyFormValues)
            return updateUser.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    const {palette} = useTheme()

    return (
        <DetailsFieldsSection
            title={t('commons:privacy')}
            isDisabled={isDisabledView}
            headingEndSlot={
                <UserDetailsEditButtons
                    groupName="privacy"
                    isConfirmDisabled={!form.formState.isDirty}
                    onCancel={form.reset}
                    onConfirm={handleSubmit}
                />
            }
        >
            {isEditModeEnabled ? (
                <UserPrivacyForm />
            ) : (
                <StyledUserDetailsFieldsGrid>
                    {user.contractType == 'employee' ? (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('users:privacy_c')}
                            </Text>
                            {user.privacyC ? (
                                <Flexbox gap={1} align="center">
                                    <CheckIcon size={16} color={palette.green[500]} />
                                    <Text size="textMd" palette="green" shade="800" weight="500">
                                        {t('commons:signed')}
                                    </Text>
                                </Flexbox>
                            ) : (
                                <Flexbox gap={1} align="center">
                                    <RefreshCw05Icon size={16} color={palette.orange[500]} />
                                    <Text size="textSm" palette="orange" shade="800">
                                        {t('errors:missing_data')}
                                    </Text>
                                </Flexbox>
                            )}
                        </DetailsField>
                    ) : (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('users:privacy_b')}
                            </Text>
                            {user.privacyB ? (
                                <Flexbox gap={1} align="center">
                                    <CheckIcon size={16} color={palette.green[500]} />
                                    <Text size="textMd" palette="green" shade="800" weight="500">
                                        {t('commons:signed')}
                                    </Text>
                                </Flexbox>
                            ) : (
                                <Flexbox gap={1} align="center">
                                    <RefreshCw05Icon size={16} color={palette.orange[500]} />
                                    <Text size="textSm" palette="orange" shade="800">
                                        {t('errors:missing_data')}
                                    </Text>
                                </Flexbox>
                            )}
                        </DetailsField>
                    )}

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('users:fm68')}
                        </Text>
                        {user.fm68 ? (
                            <Flexbox gap={1} align="center">
                                <CheckIcon size={16} color={palette.green[800]} />
                                <Text size="textMd" palette="green" shade="800" weight="500">
                                    {t('commons:signed')}
                                </Text>
                            </Flexbox>
                        ) : (
                            <Flexbox gap={1} align="center">
                                <RefreshCw05Icon size={16} color={palette.orange[500]} />
                                <Text size="textSm" palette="orange" shade="800">
                                    {t('errors:missing_data')}
                                </Text>
                            </Flexbox>
                        )}
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:reference_year')}
                        </Text>
                        <Text size="textMd" weight="500">
                            {user.privacyYear ?? '-'}
                        </Text>
                    </DetailsField>
                </StyledUserDetailsFieldsGrid>
            )}
        </DetailsFieldsSection>
    )
}
